import {inject, Injectable} from '@angular/core';
import {LogLevel} from './log-level.enum';
import {LoggingPublisherInterface} from './logging-publisher.interface';
import {LogMessage} from './log-message';
import {Logger} from './logger';
import {environment} from "../../../environments/environment";
import {LoggingPublisherConsoleService} from "./logging-publisher-console.service";
import {LoggingPublisherApiService} from "./logging-publisher-api.service";
import {isDefined} from "../../libs/utils/epic.utils";

@Injectable({providedIn: 'root'})
export class LoggingService implements Logger {

  private readonly logConsole: LoggingPublisherConsoleService = inject(LoggingPublisherConsoleService);
  private readonly logApi: LoggingPublisherApiService = inject(LoggingPublisherApiService);

  private readonly logLevel: LogLevel = environment.loglevel;
  private readonly enabledLevels: LogLevel[] = [];
  private publishers: LoggingPublisherInterface[] = [];

  constructor() {
    this.publishers.push(this.logConsole);
    this.publishers.push(this.logApi);
    this.loadLogLevel();
  }

  public isTraceEnabled(): boolean {
    return this.enabledLevels.includes(LogLevel.TRACE);
  }

  public isDebugEnabled(): boolean {
    return this.enabledLevels.includes(LogLevel.DEBUG);
  }

  public isInfoEnabled(): boolean {
    return this.enabledLevels.includes(LogLevel.INFO);
  }

  public isWarnEnabled(): boolean {
    return this.enabledLevels.includes(LogLevel.WARN);
  }

  public isErrorEnabled(): boolean {
    return this.enabledLevels.includes(LogLevel.ERROR);
  }

  public trace(message: string, params?: any[], clazz?: string, type?: string, stack?: string, code?: string | number): void {
    if (this.isTraceEnabled()) {
      const st = isDefined(stack) ? stack : new Error('').stack || '';
      this.publish(LogLevel.TRACE, st, message, params, clazz, type, code);
    }
  }

  public debug(message: string, params?: any[], clazz?: string, type?: string, stack?: string, code?: string | number): void {
    if (this.isDebugEnabled()) {
      const st = isDefined(stack) ? stack : new Error('').stack || '';
      this.publish(LogLevel.DEBUG, st, message, params, clazz, type, code);
    }
  }

  public info(message: string, params?: any[], clazz?: string, type?: string, stack?: string, code?: string | number): void {
    if (this.isInfoEnabled()) {
      const st = isDefined(stack) ? stack : new Error('').stack || '';
      this.publish(LogLevel.INFO, st, message, params, clazz, type, code);
    }
  }

  public warn(message: string, params?: any[], clazz?: string, type?: string, stack?: string, code?: string | number): void {
    if (this.isWarnEnabled()) {
      const st = isDefined(stack) ? stack : new Error('').stack || '';
      this.publish(LogLevel.WARN, st, message, params, clazz, type, code);
    }
  }

  public error(message: string, params?: any[], clazz?: string, type?: string, stack?: string, code?: string | number): void {
    if (this.isErrorEnabled()) {
      const st = isDefined(stack) ? stack : new Error('').stack || '';
      this.publish(LogLevel.ERROR, st, message, params, clazz, type, code);
    }
  }

  public throwError(message: string, params?: any[], clazz?: string, type?: string, stack?: string, code?: string | number): void {
    const st = isDefined(stack) ? stack : new Error('').stack || '';
    const msg = this.publish(LogLevel.ERROR, st, message, params, clazz, type, code);
    throw new Error(msg.buildMessage());
  }

  private publish(level: LogLevel, stack: string, message: string, params?: any[], clazz?: string, type?: string, code?: string | number): LogMessage {
    const log = new LogMessage(level, message, params, stack, clazz, type, code);
    this.publishers
      .filter((p) => p.isEnabled())
      .forEach((p) => p.publish(log));
    return log;
  }

  private loadLogLevel(): void {
    switch (this.logLevel) {
      // @ts-ignore - use fallthrough
      case LogLevel.TRACE:
        this.enabledLevels.push(LogLevel.TRACE);
      // @ts-ignore - use fallthrough
      case LogLevel.DEBUG:
        this.enabledLevels.push(LogLevel.DEBUG);
      // @ts-ignore - use fallthrough
      case LogLevel.INFO:
        this.enabledLevels.push(LogLevel.INFO);
      // @ts-ignore - use fallthrough
      case LogLevel.WARN:
        this.enabledLevels.push(LogLevel.WARN);
      // @ts-ignore - use fallthrough
      case LogLevel.ERROR:
        this.enabledLevels.push(LogLevel.ERROR);
        break;
      case LogLevel.OFF:
        console.log("Logging is disabled");
        break;
      default:
        console.log("LogLevel not set. Logging is disabled");
        break;
    }
  }
}
